import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./ProductDetails.css";
import { API_BASE_URL } from "../config/api";
import { resolveImageUrl } from "../utils/image";
import Navbar from "./Navbar";
import Footer from "./Footer";

const COLORS_PER_BATCH = 20;

export default function ProductDetail() {
    const CART_STORAGE_KEY = "rungdo_cart_v1";
    const PRODUCTS_CACHE_KEY = "estore_products_cache_v1";
    const WHATSAPP_NUMBER = "923147864360";
    const STRIPE_CHECKOUT_ENDPOINT = `${API_BASE_URL}/api/payments/create-checkout-session`;
    const { slug } = useParams();
    const navigate = useNavigate();

    const readCachedProducts = () => {
        if (typeof window === "undefined") return [];
        try {
            const saved = JSON.parse(localStorage.getItem(PRODUCTS_CACHE_KEY) || "[]");
            return Array.isArray(saved) ? saved : [];
        } catch (error) {
            return [];
        }
    };

    const getCachedProductBySlug = (targetSlug) =>
        readCachedProducts().find((item) => item.slug === targetSlug) || null;

    const [product, setProduct] = useState(() => getCachedProductBySlug(slug));
    const [selectedSize, setSelectedSize] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedQuantity, setSelectedQuantity] = useState(1);
    const [isLoading, setIsLoading] = useState(() => !getCachedProductBySlug(slug));
    const [errorMessage, setErrorMessage] = useState("");
    const [actionMessage, setActionMessage] = useState("");
    const [actionError, setActionError] = useState("");
    const [checkoutError, setCheckoutError] = useState("");
    const [isStripeLoading, setIsStripeLoading] = useState(false);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [cartSummary, setCartSummary] = useState({ products: 0, items: 0 });
    const [colorBatchIndex, setColorBatchIndex] = useState(0);

    useEffect(() => {
        let isMounted = true;
        const cachedProducts = readCachedProducts();
        const cachedProduct = cachedProducts.find((item) => item.slug === slug) || null;

        const setSelectionDefaults = (targetProduct) => {
            if (targetProduct?.sizes?.length) {
                setSelectedSize(targetProduct.sizes[0].size);
            } else {
                setSelectedSize(null);
            }
            if (targetProduct?.colors?.length) {
                setSelectedColor(targetProduct.colors[0].hex);
            } else {
                setSelectedColor(null);
            }
            setSelectedQuantity(1);
            setColorBatchIndex(0);
        };

        const buildRelatedProducts = (targetProduct, sourceProducts) => {
            if (!targetProduct?.category) return [];
            return sourceProducts
                .filter(
                    (item) =>
                        item.slug !== targetProduct.slug &&
                        item.category &&
                        item.category === targetProduct.category
                )
                .slice(0, 12);
        };

        if (cachedProduct) {
            setProduct(cachedProduct);
            setSelectionDefaults(cachedProduct);
            setRelatedProducts(buildRelatedProducts(cachedProduct, cachedProducts));
            setErrorMessage("");
            setIsLoading(false);
        } else {
            setProduct(null);
            setSelectionDefaults(null);
            setRelatedProducts([]);
            setErrorMessage("");
            setIsLoading(true);
        }

        const loadProductData = async () => {
            try {
                const productRes = await axios.get(`${API_BASE_URL}/api/products/slug/${slug}`, {
                    timeout: 10000,
                });
                if (!isMounted) return;

                const fetchedProduct = productRes.data;
                setProduct(fetchedProduct);
                setSelectionDefaults(fetchedProduct);
                setErrorMessage("");
                setIsLoading(false);

                try {
                    const allProductsRes = await axios.get(`${API_BASE_URL}/api/products`, {
                        timeout: 10000,
                    });
                    if (!isMounted) return;

                    const allProducts = Array.isArray(allProductsRes.data) ? allProductsRes.data : [];
                    setRelatedProducts(buildRelatedProducts(fetchedProduct, allProducts));
                } catch (error) {
                    if (!isMounted) return;
                    // Keep cached related products (if any) when related API is slow/unavailable.
                }
            } catch (error) {
                if (!isMounted) return;
                if (!cachedProduct) {
                    setProduct(null);
                    setRelatedProducts([]);
                    setErrorMessage("Product is not available.");
                }
                setIsLoading(false);
            }
        };

        loadProductData();

        return () => {
            isMounted = false;
        };
    }, [slug]);

    useEffect(() => {
        const refreshCartSummary = () => {
            try {
                const saved = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || "[]");
                const safeList = Array.isArray(saved) ? saved : [];
                const totalItems = safeList.reduce(
                    (count, item) => count + (Number(item.quantity) || 1),
                    0
                );
                setCartSummary({ products: safeList.length, items: totalItems });
            } catch (error) {
                setCartSummary({ products: 0, items: 0 });
            }
        };

        refreshCartSummary();
        window.addEventListener("storage", refreshCartSummary);
        window.addEventListener("cart-updated", refreshCartSummary);
        return () => {
            window.removeEventListener("storage", refreshCartSummary);
            window.removeEventListener("cart-updated", refreshCartSummary);
        };
    }, []);

    const selectedSizeDetails = product?.sizes?.find((s) => s.size === selectedSize);
    const selectedColorDetails = product?.colors?.find((c) => c.hex === selectedColor);
    const totalPrice = (Number(selectedSizeDetails?.price) || 0) * selectedQuantity;
    const totalColorBatches = Math.ceil((product?.colors?.length || 0) / COLORS_PER_BATCH);
    const visibleColors = (product?.colors || []).slice(
        colorBatchIndex * COLORS_PER_BATCH,
        (colorBatchIndex + 1) * COLORS_PER_BATCH
    );
    const hasPreviousColorBatches = colorBatchIndex > 0;
    const hasMoreColorBatches = colorBatchIndex < totalColorBatches - 1;
    const showingStartColor = product?.colors?.length ? colorBatchIndex * COLORS_PER_BATCH + 1 : 0;
    const showingEndColor = Math.min((colorBatchIndex + 1) * COLORS_PER_BATCH, product?.colors?.length || 0);

    const buildCartItem = () => {
        if (!product) return null;
        return {
            productId: product._id,
            slug: product.slug,
            name: product.name,
            image: product.image || "",
            category: product.category || "",
            size: selectedSizeDetails?.size || "",
            price: selectedSizeDetails?.price || 0,
            colorName: selectedColorDetails?.name || "",
            colorHex: selectedColorDetails?.hex || "",
            quantity: selectedQuantity,
        };
    };

    const addItemToCart = () => {
        setActionMessage("");
        setActionError("");

        if (!selectedSizeDetails) {
            setActionError("Please select a size first.");
            return null;
        }

        const itemToAdd = buildCartItem();
        if (!itemToAdd) return null;

        try {
            const existingCart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || "[]");
            const index = existingCart.findIndex(
                (item) =>
                    item.slug === itemToAdd.slug &&
                    item.size === itemToAdd.size &&
                    item.colorHex === itemToAdd.colorHex
            );

            if (index >= 0) {
                existingCart[index].quantity += selectedQuantity;
            } else {
                existingCart.push(itemToAdd);
            }

            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(existingCart));
            window.dispatchEvent(new Event("cart-updated"));
            setActionMessage("Item added to cart.");
            return itemToAdd;
        } catch (error) {
            setActionError("Cart update failed. Please try again.");
            return null;
        }
    };

    const handleAddToCart = () => {
        addItemToCart();
    };

    const buildCheckoutItem = () => {
        setActionError("");
        setCheckoutError("");
        if (!selectedSizeDetails) {
            setActionError("Please select a size first.");
            return null;
        }
        return buildCartItem();
    };

    const checkoutWithStripe = async () => {
        const item = buildCheckoutItem();
        if (!item) return;

        setIsStripeLoading(true);
        try {
            const payload = {
                items: [
                    {
                        productId: item.productId || "",
                        name: item.name,
                        size: item.size || "",
                        colorName: item.colorName || "",
                        quantity: Number(item.quantity) || 1,
                        unitPrice: Number(item.price) || 0,
                        image: item.image || "",
                        slug: item.slug || "",
                    },
                ],
                successUrl: `${window.location.origin}/checkout/success`,
                cancelUrl: `${window.location.origin}/checkout/cancel`,
            };

            const res = await axios.post(STRIPE_CHECKOUT_ENDPOINT, payload);
            const checkoutUrl = res?.data?.url;
            if (!checkoutUrl) {
                throw new Error("Stripe checkout URL missing");
            }
            window.location.href = checkoutUrl;
        } catch (error) {
            setCheckoutError("Stripe checkout is not available in your country. Please use WhatsApp checkout.");
        } finally {
            setIsStripeLoading(false);
        }
    };

    const checkoutOnWhatsApp = () => {
        const item = buildCheckoutItem();
        if (!item) return;

        const orderText = [
            "Hi, I want to buy this paint product:",
            `Product: ${item.name}`,
            `Size: ${item.size}`,
            `Quantity: ${item.quantity}`,
            `Unit Price: Rs ${item.price}`,
            `Total: Rs ${item.price * item.quantity}`,
            item.colorName ? `Color: ${item.colorName}` : "",
            `Link: ${window.location.href}`,
        ]
            .filter(Boolean)
            .join("\n");

        const whatsappShareUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(orderText)}`;
        window.open(whatsappShareUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="product-page">
            <Navbar />
            <main className="product-page-main">
                {errorMessage && !isLoading && <p className="product-detail-error">{errorMessage}</p>}
                {!isLoading && !errorMessage && !product && (
                    <p className="product-detail-status">Product not found.</p>
                )}

                {!isLoading && !errorMessage && product && (
                    <>
                        <div className="product-detail-topbar">
                            <button className="product-detail-back" onClick={() => navigate(-1)}>
                                {"\u2190"}
                            </button>
                        </div>

                        <div className="product-detail-container">
                            <div className="product-detail-left">
                                {product.image && (
                                    <img
                                        src={resolveImageUrl(product.image)}
                                        alt={product.name}
                                        className="product-detail-img"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                )}
                            </div>
                            <div className="product-detail-right">
                                <h2 className="product-detail-name">{product.name}</h2>
                                <p className="product-detail-desc">{product.description}</p>

                                {selectedSizeDetails && (
                                    <div className="product-price-box">
                                        <p className="price-label">Selected Price</p>
                                        <p className="price-value">Rs {selectedSizeDetails.price}</p>
                                        <p className="price-total">
                                            Total ({selectedQuantity}): Rs {totalPrice}
                                        </p>
                                    </div>
                                )}

                                {product.sizes && product.sizes.length > 0 && (
                                    <div className="product-detail-sizes">
                                        <h4>Select Size:</h4>
                                        <div className="sizes-list">
                                            {product.sizes.map((s, idx) => (
                                                <button
                                                    key={idx}
                                                    className={`size-btn ${selectedSize === s.size ? "selected" : ""}`}
                                                    onClick={() => setSelectedSize(s.size)}
                                                >
                                                    {s.size}: Rs {s.price}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {product.colors && product.colors.length > 0 && (
                                    <div className="product-detail-colors">
                                        <h4>Select Color:</h4>
                                        <div className="colors-list">
                                            {visibleColors.map((c, idx) => (
                                                <button
                                                    key={`${c.hex || "color"}-${c.name || idx}-${idx}`}
                                                    type="button"
                                                    className={`color-btn ${selectedColor === c.hex ? "selected" : ""}`}
                                                    style={{ backgroundColor: c.hex }}
                                                    title={c.name}
                                                    onClick={() => setSelectedColor(c.hex)}
                                                    aria-label={c.name || "Color option"}
                                                />
                                            ))}
                                        </div>
                                        {product.colors.length > COLORS_PER_BATCH && (
                                            <div className="colors-batch-controls">
                                                <p className="colors-batch-meta">
                                                    Showing {showingStartColor}-{showingEndColor} of{" "}
                                                    {product.colors.length} shades
                                                </p>
                                                <div className="colors-batch-buttons">
                                                    <button
                                                        type="button"
                                                        className="colors-view-more-btn"
                                                        onClick={() =>
                                                            setColorBatchIndex((prev) => Math.max(0, prev - 1))
                                                        }
                                                        disabled={!hasPreviousColorBatches}
                                                    >
                                                        Prev Colors
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="colors-view-more-btn"
                                                        onClick={() => setColorBatchIndex((prev) => prev + 1)}
                                                        disabled={!hasMoreColorBatches}
                                                    >
                                                        View More Colors
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {selectedColorDetails?.name && (
                                            <p className="selected-color-name">
                                                Selected Color: {selectedColorDetails.name}
                                            </p>
                                        )}
                                    </div>
                                )}
                                <div className="product-quantity-wrap">
                                    <h4>Quantity:</h4>
                                    <div className="product-qty-controls">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedQuantity((prev) => Math.max(1, prev - 1))}
                                        >
                                            -
                                        </button>
                                        <span>{selectedQuantity}</span>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedQuantity((prev) => Math.min(99, prev + 1))}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                <div className="product-detail-actions">
                                    <button type="button" className="add-to-cart-btn" onClick={handleAddToCart}>
                                        Add to Cart
                                    </button>
                                    <button
                                        type="button"
                                        className="buy-now-btn"
                                        onClick={checkoutWithStripe}
                                        disabled={isStripeLoading}
                                    >
                                        {isStripeLoading ? "Redirecting to Stripe..." : "Checkout with Card (Stripe)"}
                                    </button>
                                    <button type="button" className="checkout-whatsapp-btn" onClick={checkoutOnWhatsApp}>
                                        Checkout on WhatsApp
                                    </button>
                                </div>
                                {actionMessage && <p className="product-action-message">{actionMessage}</p>}
                                {checkoutError && <p className="product-action-error">{checkoutError}</p>}
                                {cartSummary.items > 0 && (
                                    <div className="product-cart-mini">
                                        <p>
                                            {cartSummary.items === 1 && cartSummary.products === 1
                                                ? "1 item is in your cart."
                                                : `${cartSummary.items} items in cart (${cartSummary.products} products)`}
                                        </p>
                                        <button
                                            type="button"
                                            className="go-to-cart-btn"
                                            onClick={() => navigate("/cart")}
                                        >
                                            Go to Cart
                                        </button>
                                    </div>
                                )}
                                {actionError && <p className="product-action-error">{actionError}</p>}
                            </div>
                        </div>

                        {relatedProducts.length > 0 && (
                            <section className="related-section">
                                <div className="related-head">
                                    <h3>Related Products</h3>
                                    <p>More options from {product.category}</p>
                                </div>
                                <div className="related-scroller">
                                    {relatedProducts.map((item) => (
                                        <article
                                            className="related-card"
                                            key={item._id || item.slug}
                                            onClick={() => navigate(`/product/${item.slug}`)}
                                        >
                                            {item.image && (
                                                <img
                                                    src={resolveImageUrl(item.image)}
                                                    alt={item.name}
                                                    className="related-img"
                                                    loading="lazy"
                                                />
                                            )}
                                            <h4>{item.name}</h4>
                                            <p>{item.category}</p>
                                            <Link
                                                to={`/product/${item.slug}`}
                                                className="related-link"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                View Details
                                            </Link>
                                        </article>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}
